import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'primereact/tag';
import SharedButton from '../../components/Shared/SharedButton';
import SearchField from '../../components/Shared/SearchField';
import DropdownComponent from '../../components/Shared/DropdownComponent';
import CustomTable from '../../components/Shared/CustomTable';
import CustomPagination from '../../components/Shared/CustomPagination';
import TableLoading from '../../components/Shared/TableLoading';
import CreateERPSupplierModal from './CreateERPSupplierModal';
import { getERPClient } from '../../services/supplyChainApi';
import { EditIcon } from '../../components/Shared/TableActionIcons';
import toast from 'react-hot-toast';

export default function ERPSuppliersMain() {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sfdaFilter, setSfdaFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const params = {
        q: search.trim() || undefined,
        limit: 100,
        offset: 0,
      };
      if (sfdaFilter === 'YES') params.sfda_licensed = true;
      if (sfdaFilter === 'NO') params.sfda_licensed = false;

      const res = await erpClient.getSuppliers(params);
      let list = Array.isArray(res) ? res : (res?.data || []);
      if (statusFilter !== 'ALL') {
        list = list.filter((s) => s.system_status === statusFilter);
      }
      setSuppliers(list);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error(t('Failed to load ERP suppliers'));
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [search, sfdaFilter, statusFilter, t]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setModalVisible(true);
  };

  const handleCreate = () => {
    setSelectedSupplier(null);
    setModalVisible(true);
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.system_status || 'ACTIVE';
    const severity = status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'danger' : 'warning';
    return <Tag value={status} severity={severity} />;
  };

  const sfdaBodyTemplate = (rowData) => {
    return rowData.sfda_licensed ? (
      <Tag value="SFDA Licensed" severity="info" icon="pi pi-check" />
    ) : (
      <Tag value="Not Licensed" severity="secondary" />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <EditIcon onClick={() => handleEdit(rowData)} />
    );
  };

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (page - 1) * perPage;
  };

  const sfdaOptions = [
    { label: t('All Suppliers'), value: 'ALL' },
    { label: t('SFDA Licensed Only'), value: 'YES' },
    { label: t('Non-SFDA Only'), value: 'NO' },
  ];

  const statusOptions = [
    { label: t('All Statuses'), value: 'ALL' },
    { label: t('ACTIVE'), value: 'ACTIVE' },
    { label: t('INACTIVE'), value: 'INACTIVE' },
    { label: t('SUSPENDED'), value: 'SUSPENDED' },
  ];

  const columnsData = [
    { body: actionBodyTemplate, header: t('actions'), style: { width: '90px' } },
    { body: sequenceBodyTemplate, header: t('no'), style: { width: '3rem' } },
    { body: (row) => row.supplier_code || row.supplier_global_id || row.id?.substring(0, 8) || '-', field: 'supplier_code', header: t('Code'), style: { width: '120px' } },
    { body: (row) => row.name_en || row.legal_name_en || row.name_ar || t('Unnamed Supplier'), field: 'name_en', header: t('English Name') },
    { body: (row) => row.name_ar || row.legal_name_ar || row.name_en || '-', field: 'name_ar', header: t('Arabic Name') },
    { body: (row) => row.corporate_status || '-', field: 'corporate_status', header: t('Corporate'), style: { width: '130px' } },
    { body: (row) => row.category_code || '-', field: 'category_code', header: t('Category'), style: { width: '130px' } },
    { body: (row) => row.vat_no || row.tax_number || '-', field: 'vat_no', header: t('Tax / VAT No.'), style: { width: '140px' } },
    { body: (row) => row.cr_no || row.cr_number || '-', field: 'cr_no', header: t('CR No.'), style: { width: '120px' } },
    { body: sfdaBodyTemplate, field: 'sfda_licensed', header: t('SFDA'), style: { width: '130px' } },
    { body: statusBodyTemplate, field: 'system_status', header: t('Status'), style: { width: '110px' } },
  ];

  const paginatedData = suppliers.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(suppliers.length / perPage) || 1;

  const handlePageChange = (newPage, newPerPage) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  };

  return (
    <div>
      <div className="flex flex-col mb-2 md:flex-row justify-between items-center gap-3">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchField
            className="w-full md:w-[280px]"
            placeholder={t('Search by Name, Code, VAT...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            handleClearClick={() => setSearch('')}
          />
          <div className="w-full sm:w-[180px]">
            <DropdownComponent
              useFormik={false}
              value={sfdaFilter}
              handleChange={(e) => setSfdaFilter(e.value)}
              options={sfdaOptions}
              placeholder={t('SFDA Filter')}
            />
          </div>
          <div className="w-full sm:w-[180px]">
            <DropdownComponent
              useFormik={false}
              value={statusFilter}
              handleChange={(e) => setStatusFilter(e.value)}
              options={statusOptions}
              placeholder={t('Status Filter')}
            />
          </div>
        </div>

        <SharedButton
          label={t('Create Supplier')}
          icon="pi pi-plus"
          onClick={handleCreate}
          buttonType="primary"
        />
      </div>

      <CustomTable
        data={paginatedData}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={perPage}
      />

      {suppliers.length > 0 && (
        <CustomPagination
          currentPage={page}
          rowsPerPage={perPage}
          totalRecords={totalPages}
          totalRecord={suppliers.length}
          onPageChange={handlePageChange}
        />
      )}

      <CreateERPSupplierModal
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        onRefresh={fetchSuppliers}
        initialData={selectedSupplier}
      />
    </div>
  );
}
